import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CaptureService {

  constructor() {}

  async onScreenshotCapture(): Promise<File | null> {
    let stream: MediaStream | null = null;

    try {
      const supportsCaptureController =
        'CaptureController' in window &&
        'setFocusBehavior' in (window as any).CaptureController.prototype;

      let controller;
      if (supportsCaptureController) {
        controller = new (window as any).CaptureController();
        controller.setFocusBehavior('focus-capturing-application');
      }

      // Request screen capture using Chrome's native API
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        selfBrowserSurface: 'include',
        ...(controller ? { controller } : {}),
      } as any);

      // Get video track from stream
      const track = stream.getVideoTracks()[0];

      // Check if ImageCapture is supported
      const file = 'ImageCapture' in window
        ? await this.captureScreenshotWithImageCapture(stream, track)
        : await this.captureScreenshotWithVideo(stream, track);

      if (!supportsCaptureController) {
        window.focus();
      }

      return file;
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        // User cancelled, don't show error
        return null;
      }
      console.error('Screenshot capture error:', error);
      throw error;
    } finally {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  }

  private async captureScreenshotWithImageCapture(stream: MediaStream, track: MediaStreamTrack): Promise<File> {
    const imageCapture = new (window as any).ImageCapture(track);

    const bitmap = await imageCapture.grabFrame();

    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext('2d');
    ctx?.drawImage(bitmap, 0, 0);

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), 'image/png');
    });

    const filename = `screenshot-${Date.now()}.png`;
    return new File([blob], filename, { type: 'image/png' });
  }

  private async captureScreenshotWithVideo(stream: MediaStream, track: MediaStreamTrack): Promise<File> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;

      video.onloadedmetadata = () => {
        setTimeout(() => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const ctx = canvas.getContext('2d');
            ctx?.drawImage(video, 0, 0);

            canvas.toBlob((blob) => {
              if (blob) {
                const filename = `screenshot-${Date.now()}.png`;
                const file = new File([blob], filename, { type: 'image/png' });
                resolve(file);
              } else {
                reject(new Error('Failed to create blob'));
              }
            }, 'image/png');
          } catch (error) {
            reject(error);
          }
        }, 100);
      };

      video.onerror = () => reject(new Error('Video error'));
    });
  }
}
